import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalMessages({ token }) {
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios
      .get(`${API}/messages`, { headers })
      .then((r) => setMessages(r.data || []))
      .catch(() => setMessages([]));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { body: draft.trim() }, { headers });
      setDraft("");
      load();
    } catch (err) {
      /* keep draft on failure */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-white/[0.08] bg-[#12161F] p-5" data-testid="portal-messages-card">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[#F25F22]" />
        <h3 className="font-display text-sm font-semibold text-slate-50">Messages with Backfill</h3>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-600">we reply fast</span>
      </div>
      <div className="mt-4 max-h-64 space-y-2.5 overflow-y-auto" data-testid="portal-msg-thread">
        {messages === null ? (
          <div className="flex items-center justify-center gap-2 py-6 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            Questions about your line, billing, or a tricky conversation? Message us here — you&apos;ll get an email when we reply.
          </p>
        ) : (
          messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.sender_role === "customer" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`portal-msg-bubble-${i}`}
                className={`max-w-[75%] rounded-xl px-3.5 py-2 text-[13px] leading-relaxed ${
                  m.sender_role === "customer"
                    ? "bg-[#F25F22] text-white"
                    : "border border-white/[0.08] bg-[#1A212E] text-slate-200"
                }`}
              >
                {m.body}
                <span className={`mt-1 block text-[9px] ${m.sender_role === "customer" ? "text-white/60" : "text-slate-500"}`}>
                  {m.sender_role === "customer" ? "You" : "Backfill"} ·{" "}
                  {new Date(m.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2 border-t border-white/[0.06] pt-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="portal-msg-input"
          placeholder="Type a message…"
          className="w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          data-testid="portal-msg-send"
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-[#F25F22] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </button>
      </form>
    </div>
  );
}
